import {
  CheckCircleOutlined,
  FileOutlined,
  PieChartOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Menu, MenuProps } from "antd";
import { useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../Context/AuthContext/Index";
import { InterviewRoundTab } from "../../Interface/Index";
import { DashboardContext } from "../../Context/DashboardContext";

type MenuItem = Required<MenuProps>["items"][number];

const sidebarStyle: React.CSSProperties = {
  borderRadius: 8,
  overflowY: "auto",
  background: "#ffffff",
  border: "1px solid #f0f0f0",
};

const SideNav = ({ onClick }: any) => {
  const { isUserLoggedIn } = useAuth();
  const { jobDescriptionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const {
    enabledInterviewRoundTabs,
    showOnlineInterviewTab,
    selectedKey,
    setSelectedKey,
    showResultTab,
  } = useContext(DashboardContext);

  const getCurrentKey = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);

    if (pathSegments.includes("interview-round")) {
      const sessionId =
        pathSegments[pathSegments.indexOf("interview-round") + 1];
      return sessionId ? `session-${sessionId}` : `requirements`;
    }

    if (pathSegments.includes("requirements")) {
      return "requirements";
    }

    if (pathSegments.includes("online-interview")) {
      return "online-interview";
    }

    if (pathSegments.includes("result")) {
      return "result";
    }

    return "requirements";
  };

  const scrollToActiveItem = (key: string, retryCount = 0) => {
    const maxRetries = 10;
    const retryDelay = 100;

    const attemptScroll = () => {
      const sidebarContainer = menuRef.current;
      if (!sidebarContainer) return false;

      const selectedItem = sidebarContainer.querySelector(
        '.ant-menu-item-selected'
      ) as HTMLElement;

      if (selectedItem) {
        scrollToElementInContainer(selectedItem, sidebarContainer);
        return true;
      }

      const allMenuItems = sidebarContainer.querySelectorAll('.ant-menu-item');
      for (const item of allMenuItems) {
        const itemKey = item.getAttribute('data-menu-id') || 
                       item.getAttribute('data-testid') ||
                       item.getAttribute('key');
        if (itemKey === key) {
          scrollToElementInContainer(item as HTMLElement, sidebarContainer);
          return true;
        }
      }

      return false;
    };

    if (attemptScroll()) {
      return;
    }

    if (retryCount < maxRetries) {
      setTimeout(() => {
        scrollToActiveItem(key, retryCount + 1);
      }, retryDelay);
    }
  };

  const scrollToElementInContainer = (element: HTMLElement, container: HTMLElement) => {
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    
    const elementTop = elementRect.top - containerRect.top + container.scrollTop;
    const elementBottom = elementTop + elementRect.height;
    
    const containerHeight = container.clientHeight;
    const containerScrollTop = container.scrollTop;
    const containerScrollBottom = containerScrollTop + containerHeight;
    
    // Check if element is not fully visible
    if (elementTop < containerScrollTop || elementBottom > containerScrollBottom) {
      // Calculate the scroll position to center the element
      const targetScrollTop = elementTop - containerHeight / 2 + elementRect.height / 2;
      
      // Smooth scroll within the container
      container.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const currentKey = getCurrentKey();
    if (currentKey !== selectedKey) {
      setSelectedKey(currentKey);
    }
    
    setTimeout(() => {
      scrollToActiveItem(currentKey);
    }, 200);
  }, [location.pathname, jobDescriptionId]);

  useEffect(() => {
    if (selectedKey) {
      setTimeout(() => {
        scrollToActiveItem(selectedKey);
      }, 100);
    }
  }, [selectedKey]);

  // Effect to handle initial mount and ensure scroll happens after Menu renders
  useEffect(() => {
    const currentKey = getCurrentKey();
    // Delay to ensure Menu component is fully mounted and rendered
    const timeoutId = setTimeout(() => {
      scrollToActiveItem(currentKey);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [enabledInterviewRoundTabs, showOnlineInterviewTab, showResultTab]);

  const handleRoundClick = (sessionId: string | null) => {
    if (!sessionId) return;
    navigate(`/${jobDescriptionId}/interview-round/${sessionId}`);
  };

  const items: MenuItem[] = [
    {
      key: "requirements",
      icon: <PieChartOutlined />,
      label: "Requirements",
      onClick: () => navigate(`/${jobDescriptionId}/requirements`),
    },
    ...(enabledInterviewRoundTabs?.length
      ? [...enabledInterviewRoundTabs]
          .sort((a, b) => a.roundNumber - b.roundNumber)
          .flatMap((round: InterviewRoundTab) => [
            {
              key: round.sessionId
                ? `session-${round.sessionId}`
                : round.roundId,
              icon: <FileOutlined />,
              label: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                  }}
                >
                  <span
                    style={{
                      maxWidth: "100%",
                      display: "block",
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {round.roundName}
                  </span>
                </div>
              ),

              disabled: !isUserLoggedIn || !round.isEnabled,
              onClick: () => handleRoundClick(round.sessionId),
            },
          ])
      : []),
    ...(showOnlineInterviewTab
      ? [
          {
            key: "online-interview",
            icon: <TeamOutlined />,
            label: "Online Interview",
            disabled: !showOnlineInterviewTab,
            onClick: () => navigate(`/${jobDescriptionId}/online-interview`),
          },
        ]
      : []),
    ...(showResultTab
      ? [
          {
            key: "result",
            icon: <CheckCircleOutlined />,
            label: "Result",
            onClick: () => navigate(`/${jobDescriptionId}/result`),
          },
        ]
      : []),
  ];

  return (
    <div 
      ref={menuRef}
      style={sidebarStyle} 
      className="dashboard_side_nav"
    >
      <Menu mode="inline" items={items} selectedKeys={[selectedKey]} />
    </div>
  );
};

export default SideNav;
